import axios from 'axios'
import { format } from 'date-fns'
import { Audit } from './types'
import { Audit as APIAudit } from '../../../../hooks/useAudit/useAudit.type'

import { saveAs } from 'file-saver'
import { getUser } from '../../../../utils/auth'
import { API_KEY, API_URL } from '../../../../constants/global'
import { AuditFilterStatus, AuditStatus } from './enums'
import { Dispatch, SetStateAction } from 'react'
import { Item } from '../../../../types'
import {
   FiltersForAPI,
   Filters as FiltersObject,
} from '../../../../hooks/useFilters/useFilters.type'
import { formatFilterDateForAPI } from '../../../../utils/datetime'
import { flattenFilterObj } from '../../../../hooks/useFilters/useFilters.helpers'

export function formatFiltersForAPI(filters: FiltersObject): FiltersForAPI {
   const startDateFilter = {
      ...(filters?.startDate?.value instanceof Date && {
         startDate: formatFilterDateForAPI(filters?.startDate?.value),
      }),
   }
   const endDateFilter = {
      ...(filters?.endDate?.value instanceof Date && {
         endDate: formatFilterDateForAPI(filters?.endDate?.value),
      }),
   }

   return {
      ...flattenFilterObj(filters),
      ...startDateFilter,
      ...endDateFilter,
   }
}

export const prepareAuditForAPI = (
   audit: Audit,
   requestType: string,
   removedAuditItemsIds: number[]
): APIAudit => {
   return {
      auditID: audit?.AuditID,
      auditedBy: audit?.AuditedByID,
      dueDate: audit?.DateDue
         ? format(new Date(audit?.DateDue), 'd MMM yyyy')
         : null,
      isSendRequest: requestType === 'send',
      RemovedAuditItemsId: removedAuditItemsIds,
      CloseAfterDueDate: audit?.CloseAfterDueDate,
      itemsForAudit: audit?.AuditItems?.map((item) => ({
         AuditItemID: item.AuditItemID || null,
         auditItemID: item.AuditItemID || null,
         toolItemId: item.Id,
         toolLoanId: item.ToolLoanId,
         kitId: item.KitId,
         statusQuantity: item.StatusQuantity,
         status: item.Status,
         quantity: item.Quantity,
         toolWarehouseId: item.Warehouse?.ToolWarehouseID,
      })),
   }
}

export const downloadCsv = async (
   path: string,
   fileName: string,
   id: number,
   showSpinnerMethod: Dispatch<SetStateAction<boolean>>
) => {
   try {
      showSpinnerMethod(true)
      const user = getUser()?.access_token

      const response = await axios.post(
         `${API_URL}${path}`,
         { offset: -1, auditID: id },
         {
            headers: {
               'Ocp-Apim-Subscription-Key': API_KEY,
               Authorization: `Bearer ${user}`,
               'Content-Type': 'application/json',
            },
            responseType: 'arraybuffer', // Ensures binary data handling
         }
      )

      const blob = new Blob([response.data], {
         type: 'application/octet-stream',
      })
      saveAs(blob, `${fileName}_${id}.xlsx`)
   } catch (error) {
      console.error('Download failed:', error)
   } finally {
      showSpinnerMethod(false)
   }
}

export const getAuditStatusId = (status: string) => {
   switch (status) {
      case 'draft':
         return AuditFilterStatus.DRAFT
      case 'sent / in progress':
         return AuditFilterStatus.SENT_IN_PROGRESS
      case 'for review':
         return AuditFilterStatus.FOR_REVIEW
      case 'completed':
         return AuditFilterStatus.COMPLETED
      case 'working':
      default:
         return AuditFilterStatus.WORKING
   }
}

export const getAuditStatusLabelForFilters = (statusId: number | string) => {
   const numberId = Number(statusId)
   switch (numberId) {
      case AuditStatus.SENT:
      case AuditStatus.IN_PROGRESS:
         return 'Sent / In Progress'
      case AuditStatus.FOR_REVIEW:
         return 'For Review'
      case AuditStatus.COMPLETED:
         return 'Completed'
      case AuditStatus.DRAFT:
      default:
         return 'Draft'
   }
}

export const itemsMatch = (item1: Item, item2: Item): boolean =>
   item1?.localUniqueId === item2?.localUniqueId &&
   item1?.DefaultLocationID === item2?.DefaultLocationID
